import React, { useEffect, useMemo, useState } from "react";
import { endpoints, loadSchema } from "./schema";
import {
  listMutations,
  listQueries,
  defaultSelectionPaths,
} from "./operationUtils";
import { generateTemplate } from "./template";
import { parseWorkbook } from "./parser";
import { executeRows } from "./executor";
import * as XLSX from "xlsx";
import ResultsTable from "./ResultsTable";
import SelectionTree from "./SelectionTree";

export default function App() {
  const endpointKeys = Object.keys(endpoints);
  const [endpoint, setEndpoint] = useState(
    localStorage.getItem("endpoint") ?? endpointKeys[0],
  );
  const [token, setToken] = useState(localStorage.getItem("token") ?? "");
  const [mutations, setMutations] = useState<string[]>([]);
  const [queries, setQueries] = useState<string[]>([]);
  const [operationType, setOperationType] = useState<"mutation" | "query">(
    (localStorage.getItem("operationType") as "mutation" | "query") ??
      "mutation",
  );
  const [operationName, setOperationName] = useState(
    localStorage.getItem("operationName") ?? "",
  );
  const [schemaSDL, setSchemaSDL] = useState("");
  const [selectionPaths, setSelectionPaths] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [parsedRows, setParsedRows] = useState<any[]>([]);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<any[]>([]);

  useEffect(() => {
    localStorage.setItem("endpoint", endpoint);
    loadSchema(endpoint)
      .then((sdl) => {
        setSchemaSDL(sdl);
        const mList = listMutations(sdl);
        const qList = listQueries(sdl);
        setMutations(mList);
        setQueries(qList);
        const curList = operationType === "mutation" ? mList : qList;
        if (!curList.includes(operationName)) {
          if (mList.length) {
            setOperationType("mutation");
            setOperationName(mList[0]);
          } else if (qList.length) {
            setOperationType("query");
            setOperationName(qList[0]);
          } else {
            setOperationName("");
          }
        }
      })
      .catch(console.error);
  }, [endpoint]);

  useEffect(() => {
    if (!schemaSDL || !operationName) return;
    setSelectionPaths(
      defaultSelectionPaths(schemaSDL, operationName, operationType),
    );
  }, [schemaSDL, operationName, operationType]);

  useEffect(() => {
    localStorage.setItem("token", token);
  }, [token]);

  useEffect(() => {
    localStorage.setItem("operationType", operationType);
    localStorage.setItem("operationName", operationName);
  }, [operationType, operationName]);

  const filteredMutations = useMemo(
    () =>
      mutations.filter((m) => m.toLowerCase().includes(search.toLowerCase())),
    [mutations, search],
  );
  const filteredQueries = useMemo(
    () => queries.filter((q) => q.toLowerCase().includes(search.toLowerCase())),
    [queries, search],
  );

  useEffect(() => {
    const currentFiltered =
      operationType === "mutation" ? filteredMutations : filteredQueries;
    if (!currentFiltered.includes(operationName)) {
      if (currentFiltered.length > 0) {
        setOperationName(currentFiltered[0]);
      }
    }
  }, [filteredMutations, filteredQueries, operationType, operationName]);

  const handleDownloadTemplate = async () => {
    const sdl = await loadSchema(endpoint);
    const wb = generateTemplate(sdl, operationName, operationType);
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${operationName}_template.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsedRows([]);
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);
      const sdl = await loadSchema(endpoint);
      const { rows, warnings } = parseWorkbook(
        wb,
        sdl,
        operationName,
        operationType,
      );
      if (warnings.length > 0) {
        alert(warnings.join("\n"));
      } else {
        setParsedRows(rows);
      }
    } catch (err) {
      setParsedRows([]);
      console.error(err);
      alert(
        `Failed to parse file: ${err instanceof Error ? err.message : err}`,
      );
    }
  };

  const handleExecute = async () => {
    const sdl = await loadSchema(endpoint);
    setProgress(0);
    const res = await executeRows(
      endpoint,
      token,
      sdl,
      operationName,
      parsedRows,
      (d) => setProgress(d),
      Array.from(selectionPaths),
      operationType,
    );
    setResults(res);
  };

  const handleDownloadResults = () => {
    if (results.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(results);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "results");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "results.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <label>
        Endpoint:
        <select value={endpoint} onChange={(e) => setEndpoint(e.target.value)}>
          {endpointKeys.map((ep) => (
            <option key={ep} value={ep}>
              {ep}
            </option>
          ))}
        </select>
      </label>
      <br />
      <label>
        Bearer Token:
        <input
          type="text"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Optional token"
        />
      </label>
      <br />
      <label>
        Operation search:
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="filter"
        />
      </label>
      <br />
      <label>
        Operation:
        <select
          value={`${operationType}:${operationName}`}
          onChange={(e) => {
            const [type, name] = e.target.value.split(":");
            setOperationType(type as "mutation" | "query");
            setOperationName(name);
          }}
        >
          <optgroup label="Mutations">
            {filteredMutations.map((m) => (
              <option key={`m-${m}`} value={`mutation:${m}`}>
                {m}
              </option>
            ))}
          </optgroup>
          <optgroup label="Queries">
            {filteredQueries.map((q) => (
              <option key={`q-${q}`} value={`query:${q}`}>
                {q}
              </option>
            ))}
          </optgroup>
        </select>
      </label>
      {schemaSDL && operationName && (
        <SelectionTree
          schemaSDL={schemaSDL}
          operationName={operationName}
          operationType={operationType}
          selected={selectionPaths}
          onChange={setSelectionPaths}
        />
      )}
      <button onClick={handleDownloadTemplate}>Download Excel Template</button>
      <br />
      <input type="file" accept=".xlsx" onChange={handleUpload} />
      <button onClick={handleExecute} disabled={parsedRows.length === 0}>
        Execute
      </button>
      <br />
      <progress value={progress} max={parsedRows.length}></progress>
      <br />
      <button onClick={handleDownloadResults} disabled={results.length === 0}>
        Download Results
      </button>
      {results.length > 0 && <ResultsTable rows={results} />}
    </>
  );
}
